import React from "react";
import { Check } from "lucide-react";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { US_STATES } from "../mock";

export const OptionButton = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-2xl border-2 px-5 py-4 text-left transition-colors duration-200 ${
      selected
        ? "border-[#2FA33F] bg-[#EAF9EA]"
        : "border-slate-200 bg-white hover:border-[#3CC54F]/60 hover:bg-[#F7FCF7]"
    }`}
  >
    <span className="pr-3 text-[15px] font-semibold text-slate-800">{children}</span>
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
        selected ? "border-[#3CC54F] bg-[#3CC54F]" : "border-slate-300 bg-white"
      }`}
    >
      {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
    </span>
  </button>
);

export const OptionList = ({ options, value, onChange, prefix }) => (
  <div className="space-y-3">
    {options.map((o) => (
      <OptionButton
        key={o.id}
        testId={`${prefix}-option-${o.id}`}
        selected={value === o.id}
        onClick={() => onChange(o.id)}
      >
        {o.desc ? (
          <>
            <span className="block">{o.label}</span>
            <span className="mt-0.5 block text-sm font-normal text-slate-500">{o.desc}</span>
          </>
        ) : (
          o.label
        )}
      </OptionButton>
    ))}
  </div>
);

export const PillGroup = ({ options, value, onChange, prefix, label }) => (
  <div>
    {label && <p className="mb-2.5 text-sm font-semibold text-slate-700">{label}</p>}
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${prefix}-pill-${o.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
          onClick={() => onChange(o)}
          className={`rounded-full border-2 px-5 py-2.5 text-[15px] font-semibold transition-colors duration-200 ${
            value === o
              ? "border-[#2FA33F] bg-[#EAF9EA] text-[#25862F]"
              : "border-slate-200 bg-white text-slate-700 hover:border-[#3CC54F]/60 hover:bg-[#F7FCF7]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

export const formatPrice = (v) =>
  v >= 1500000 ? "$1.5M+" : `$${v.toLocaleString("en-US")}`;

export const PriceSlider = ({ value, onChange }) => (
  <div className="pt-2">
    <p data-testid="home-price-value" className="text-center font-heading text-4xl font-extrabold text-[#2FA33F]">
      {formatPrice(value)}
    </p>
    <Slider
      data-testid="home-price-slider"
      className="mt-6 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-[#3CC54F] [&_[role=slider]]:bg-white [&_.bg-primary]:bg-[#3CC54F] [&_.bg-primary\\/20]:bg-[#3CC54F]/20"
      min={100000}
      max={1500000}
      step={25000}
      value={[value]}
      onValueChange={([v]) => onChange(v)}
    />
    <div className="mt-3 flex justify-between text-xs font-semibold text-slate-400">
      <span>$100k</span>
      <span>$500k</span>
      <span>$1M</span>
      <span>$1.5M+</span>
    </div>
  </div>
);

export const LocationFields = ({ form, set }) => (
  <div className="space-y-4">
    <div>
      <Label className="text-sm font-medium text-slate-700">State *</Label>
      <select
        data-testid="lead-state"
        value={form.state}
        onChange={(e) => set("state", e.target.value)}
        className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-[15px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3CC54F]/40"
      >
        <option value="">Pick your state…</option>
        {US_STATES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    </div>
    <div>
      <Label className="text-sm font-medium text-slate-700">City *</Label>
      <Input
        data-testid="lead-city"
        className="mt-1.5 h-11 rounded-xl"
        placeholder="e.g. Arlington, Tampa, Charlotte"
        value={form.city}
        onChange={(e) => set("city", e.target.value)}
      />
    </div>
  </div>
);

export const ContactFields = ({ form, set }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    <div>
      <Label className="text-sm font-medium text-slate-700">First Name *</Label>
      <Input data-testid="lead-first-name" className="mt-1.5 h-11 rounded-xl" placeholder="Jordan" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
    </div>
    <div>
      <Label className="text-sm font-medium text-slate-700">Last Name *</Label>
      <Input data-testid="lead-last-name" className="mt-1.5 h-11 rounded-xl" placeholder="Rivera" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
    </div>
    <div className="sm:col-span-2">
      <Label className="text-sm font-medium text-slate-700">Email *</Label>
      <Input data-testid="lead-email" type="email" className="mt-1.5 h-11 rounded-xl" placeholder="you@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
    </div>
    <div className="sm:col-span-2">
      <Label className="text-sm font-medium text-slate-700">Phone *</Label>
      <Input data-testid="lead-phone" type="tel" className="mt-1.5 h-11 rounded-xl" placeholder="(555) 123-4567" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
    </div>
  </div>
);
